import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Badge,
  Autocomplete,
} from '@mui/material';
import {
  Delete,
  Add,
  Search,
  FilterList,
  ArrowBack,
  Close,
  LocalOffer,
  CalendarToday,
  AttachMoney,
  Refresh,
  Visibility,
  Whatshot,
  Edit,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListFlashSalesQuery,
  useAddFlashSaleMutation,
  useUpdateFlashSaleMutation,
  useDeleteFlashSaleMutation,
} from '../../features/admin/flashSaleApi.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

interface FlashSale {
  id: number;
  flash_sale_id?: number; // For compatibility with backend response
  product_id: string | number;
  product_price: string;
  start_date: string;
  end_date: string;
  status: string;
  sale_status?: string;
  product_name?: string;
}

interface EnrichedFlashSale extends FlashSale {
  product_name?: string;
  product_image?: string;
  prod_name?: string;
  product_main_image?: string;
  original_price?: number;
  currency?: string;
  flash_sale_id?: number; // Added for backend compatibility
  vendor_id?: number | string | null;
  vendor_name?: string | null;
}

interface FlashSaleFormData {
  product_id: string;
  product_price: string;
  start_date: string;
  end_date: string;
  active: boolean;
}

const getVendorId = (item: any): number | string | null => {
  const value = item?.vendor_id ?? item?.vendorId ?? item?.seller_id ?? item?.sellerId;
  return value === undefined || value === null || value === '' ? null : value;
};

const getVendorName = (item: any): string | null => {
  const value = item?.vendor_name ?? item?.vendorName ?? item?.business_name ?? item?.vendor_business_name;
  return value ? String(value) : null;
};

const FlashSale = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [productSearchStr, setProductSearchStr] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [enrichedFlashSales, setEnrichedFlashSales] = useState<EnrichedFlashSale[]>([]);
  const [formData, setFormData] = useState<FlashSaleFormData>({
    product_id: '',
    product_price: '',
    start_date: '',
    end_date: '',
    active: true,
  });
  const [formErrors, setFormErrors] = useState<Partial<FlashSaleFormData>>({});
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [tabValue, setTabValue] = useState(0);
  const [filteredSales, setFilteredSales] = useState<EnrichedFlashSale[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Admin filter: show Flash Sales belonging to one vendor by name or ID.
  const [vendorSearchStr, setVendorSearchStr] = useState('');
  const [productVendorSearchStr, setProductVendorSearchStr] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSaleId, setEditingSaleId] = useState<number | null>(null);

  // ============================================
  // ROLE-BASED ACCESS CONTROL
  // ============================================
  const rawUserRole = localStorage.getItem('admin_type') || localStorage.getItem('user_role') || '';
  const userRole = rawUserRole.toLowerCase().trim();

  const isSuperAdmin = userRole.includes('super');
  const isAdmin = userRole === 'admin';
  const isOperation = userRole === 'operation';
  const isVendor = userRole === 'vendor';

  const hasFlashSaleAccess = isSuperAdmin || isAdmin;
  const hasFlashSaleWrite = isSuperAdmin || isAdmin;

  console.log('🔐 Flash Sale Access Control:', {
    rawUserRole,
    userRole,
    isSuperAdmin,
    isAdmin,
    isOperation,
    isVendor,
    hasFlashSaleAccess,
    hasFlashSaleWrite
  });

  // ============================================
  // ACCESS DENIED COMPONENT
  // ============================================
  const AccessDenied = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Card sx={{ 
              maxWidth: 500, 
              p: 4, 
              textAlign: 'center',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: '50%', 
                  bgcolor: '#ffebee', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  <Whatshot sx={{ fontSize: 48, color: '#f44336' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223', mb: 1 }}>
                  Access Denied
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You don't have permission to access Flash Sale Management.
                </Typography>
                <Chip 
                  label={`Your Role: ${rawUserRole || 'Not Set'}`}
                  sx={{ 
                    bgcolor: isOperation ? '#ff9800' : isVendor ? '#4caf50' : isSuperAdmin ? '#f44336' : '#2196f3',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Required Access Level:
                </Typography>
                <Typography variant="body2">
                  • Super Admin ✓<br />
                  • Admin ✓
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={() => {
                  if (isOperation || isVendor) {
                    window.location.href = '/productManagement';
                  } else {
                    window.location.href = '/adminDashboard';
                  }
                }}
                sx={{
                  bgcolor: '#008060',
                  textTransform: 'none',
                  fontWeight: 600,
                  width: '100%',
                  '&:hover': { bgcolor: '#006e52' }
                }}
              >
                Go to {isOperation || isVendor ? 'Product Management' : 'Dashboard'}
              </Button>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // If user doesn't have access, show access denied
  if (!hasFlashSaleAccess) {
    return <AccessDenied />;
  }

  // ============================================
  // API HOOKS
  // ============================================
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListFlashSalesQuery({});

  const {
    data: productsResponse,
    isLoading: isLoadingProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 50,
    search_str: productSearchStr
  });

  const {
    data: allProductsResponse,
    isLoading: isLoadingAllProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 1000,
    search_str: ''
  });

  const [addFlashSale, { isLoading: isAdding }] = useAddFlashSaleMutation();
  const [updateFlashSale, { isLoading: isUpdating }] = useUpdateFlashSaleMutation();
  const [deleteFlashSale, { isLoading: isDeleting }] = useDeleteFlashSaleMutation();

  const products = productsResponse?.result || productsResponse?.data || [];
  const allProducts = allProductsResponse?.result || allProductsResponse?.data || [];
  const vendorFilteredProducts = productVendorSearchStr.trim()
    ? allProducts.filter((product: Product) => {
        const query = productVendorSearchStr.trim().toLowerCase();
        const vendorName = getVendorName(product)?.toLowerCase() || '';
        const vendorId = getVendorId(product)?.toString().toLowerCase() || '';
        return vendorName.includes(query) || vendorId.includes(query);
      })
    : products;

  // When vendor search is active, search the complete product list so every product
  // belonging to that vendor is available, not only the first 50 API results.
  const productOptions = productVendorSearchStr.trim()
    ? vendorFilteredProducts.filter((product: Product) => {
        const q = productSearchStr.trim().toLowerCase();
        if (!q) return true;
        return (product.prod_name || '').toLowerCase().includes(q) ||
          (product.product_id || product.id || '').toString().includes(q);
      })
    : products;

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const fixDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      let date: Date;
      
      // Check if it's already in ISO format
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        // Try parsing as ISO
        date = new Date(dateString);
        
        // If invalid, try adding timezone
        if (isNaN(date.getTime())) {
          date = new Date(dateString + 'Z');
        }
      }
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return '';
      }
      
      // Format as YYYY-MM-DDTHH:MM for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error fixing date:', error, dateString);
      return '';
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'active' || statusLower === 'ongoing') {
      return { bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 };
    } else if (statusLower === 'upcoming') {
      return { bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 };
    } else if (statusLower === 'expired' || statusLower === 'inactive') {
      return { bgcolor: '#ffcdd2', color: '#c62828', fontWeight: 600 };
    }
    return { bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600 };
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return 'AED 0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AED',
    }).format(numAmount);
  };

  const calculateDiscount = (original: number, sale: number) => {
    if (!original || original <= 0) return 0;
    const saleNum = typeof sale === 'string' ? parseFloat(sale) : sale;
    const discount = ((original - saleNum) / original) * 100;
    return Math.round(discount);
  };

  const getTabCount = (tabIndex: number) => {
    if (tabIndex === 0) return enrichedFlashSales.length;
    if (tabIndex === 1) return enrichedFlashSales.filter(s => 
      (s.sale_status || s.status)?.toLowerCase() === 'active' || 
      (s.sale_status || s.status)?.toLowerCase() === 'ongoing'
    ).length;
    if (tabIndex === 2) return enrichedFlashSales.filter(s => 
      (s.sale_status || s.status)?.toLowerCase() === 'upcoming'
    ).length;
    if (tabIndex === 3) return enrichedFlashSales.filter(s => 
      (s.sale_status || s.status)?.toLowerCase() === 'expired' ||
      (s.sale_status || s.status)?.toLowerCase() === 'inactive'
    ).length;
    return 0;
  };

  // ============================================
  // EFFECTS
  // ============================================
  
  // Debug logging
  useEffect(() => {
    if (isError) {
      console.error('❌ Flash sale query error:', error);
    }
    if (data) {
      console.log('✅ Flash sale data structure:', {
        data,
        dataType: typeof data,
        isArray: Array.isArray(data),
        dataKeys: Object.keys(data || {}),
        dataData: data?.data,
        isDataArray: Array.isArray(data?.data),
        firstItem: Array.isArray(data) ? data[0] : data?.data?.[0]
      });
    }
  }, [data, isError, error]);

  // Debug edit mode
  useEffect(() => {
    if (openAddDialog && isEditMode && selectedProduct) {
      console.log('📅 Edit mode debug:', {
        formData,
        selectedProduct,
        editingSaleId,
        originalSale: enrichedFlashSales.find(s => (s.id || s.flash_sale_id) === editingSaleId),
      });
    }
  }, [openAddDialog, isEditMode, formData, selectedProduct, editingSaleId]);

  // Enrich flash sales with product details
  useEffect(() => {
    console.log('🔄 Processing flash sales data...');
    
    let rawFlashSales: any[] = [];
    
    if (Array.isArray(data)) {
      rawFlashSales = data;
    } else if (data?.data && Array.isArray(data.data)) {
      rawFlashSales = data.data;
    } else if (data?.result && Array.isArray(data.result)) {
      rawFlashSales = data.result;
    } else if (data?.data?.data && Array.isArray(data.data.data)) {
      rawFlashSales = data.data.data;
    }
    
    console.log('📊 Raw flash sales count:', rawFlashSales.length);
    
    if (rawFlashSales.length > 0 && allProducts.length > 0) {
      const enriched = rawFlashSales.map((sale: any) => {
        const saleId = sale.id || sale.flash_sale_id;
        const productId = sale.product_id;
        
        const product = allProducts.find((p: Product) => {
          const pId = p.product_id || p.id;
          return pId == productId;
        });

        return {
          id: saleId,
          flash_sale_id: saleId,
          product_id: productId,
          product_price: sale.product_price || sale.price || '0',
          start_date: sale.start_date,
          end_date: sale.end_date,
          status: sale.status || sale.flash_sale_status || 'active',
          sale_status: sale.sale_status || sale.status,
          product_name: sale.product_name || sale.name || product?.prod_name || 'Unknown Product',
          product_image: sale.product_image || sale.image || product?.product_main_image || '',
          prod_name: sale.prod_name || product?.prod_name || '',
          product_main_image: sale.product_main_image || product?.product_main_image || '',
          original_price: product?.product_price || 0,
          currency: product?.currency || 'AED',
          vendor_id: getVendorId(sale) ?? getVendorId(product),
          vendor_name: getVendorName(sale) ?? getVendorName(product),
        };
      });
      
      console.log('🎯 Enriched flash sales:', enriched.length);
      setEnrichedFlashSales(enriched);
    } else if (rawFlashSales.length === 0) {
      console.log('📭 No flash sales data found');
      setEnrichedFlashSales([]);
    }
  }, [data, allProducts]);

  // Filter sales based on tab and search
  useEffect(() => {
    let filtered = [...enrichedFlashSales];

    if (tabValue === 1) {
      filtered = filtered.filter(sale => 
        (sale.sale_status || sale.status)?.toLowerCase() === 'active' ||
        (sale.sale_status || sale.status)?.toLowerCase() === 'ongoing'
      );
    } else if (tabValue === 2) {
      filtered = filtered.filter(sale => 
        (sale.sale_status || sale.status)?.toLowerCase() === 'upcoming'
      );
    } else if (tabValue === 3) {
      filtered = filtered.filter(sale => 
        (sale.sale_status || sale.status)?.toLowerCase() === 'expired' ||
        (sale.sale_status || sale.status)?.toLowerCase() === 'inactive'
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sale =>
        (sale.product_name?.toLowerCase().includes(query)) ||
        (sale.prod_name?.toLowerCase().includes(query)) ||
        (sale.product_id?.toString().includes(query)) ||
        (sale.id?.toString().includes(query)) ||
        (sale.flash_sale_id?.toString().includes(query)) ||
        (sale.vendor_name?.toLowerCase().includes(query)) ||
        (sale.vendor_id?.toString().includes(query))
      );
    }

    // Admin can narrow the list to one vendor using business name or vendor ID.
    if (vendorSearchStr.trim()) {
      const vendorQuery = vendorSearchStr.trim().toLowerCase();
      filtered = filtered.filter(sale =>
        sale.vendor_name?.toLowerCase().includes(vendorQuery) ||
        sale.vendor_id?.toString().includes(vendorQuery)
      );
    }

    setFilteredSales(filtered);
  }, [enrichedFlashSales, tabValue, searchQuery, vendorSearchStr]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRefresh = () => {
    refetch();
    setSnackbar({
      open: true,
      message: 'Flash sales refreshed successfully!',
      severity: 'success'
    });
  };

  const handleOpenAddDialog = () => {
    if (!hasFlashSaleWrite) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to add flash sales.',
        severity: 'error'
      });
      return;
    }

    // Reset edit mode
    setIsEditMode(false);
    setEditingSaleId(null);
    
    // Set default dates (tomorrow to day after tomorrow)
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0); // Set to 10:00 AM
    
    const dayAfterTomorrow = new Date(tomorrow);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
    dayAfterTomorrow.setHours(22, 0, 0, 0); // Set to 10:00 PM

    setOpenAddDialog(true);
    setSelectedProduct(null);
    setProductSearchStr('');
    setProductVendorSearchStr('');
    setFormData({
      product_id: '',
      product_price: '',
      start_date: fixDateForInput(tomorrow.toISOString()),
      end_date: fixDateForInput(dayAfterTomorrow.toISOString()),
      active: true,
    });
    setFormErrors({});
  };

  const handleEditFlashSale = (sale: EnrichedFlashSale) => {
    if (!hasFlashSaleWrite) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to edit flash sales.',
        severity: 'error'
      });
      return;
    }

    // Find the product for this flash sale
    const product = allProducts.find((p: Product) => 
      (p.product_id || p.id) == sale.product_id
    );
    
    console.log('✏️ Editing flash sale:', {
      sale,
      product,
      saleStartDate: sale.start_date,
      saleEndDate: sale.end_date,
      fixedStartDate: fixDateForInput(sale.start_date),
      fixedEndDate: fixDateForInput(sale.end_date)
    });
    
    setSelectedProduct(product || null);
    setEditingSaleId(sale.id || sale.flash_sale_id || null);
    setIsEditMode(true);
    
    setFormData({
      product_id: sale.product_id.toString(),
      product_price: sale.product_price.toString(),
      start_date: fixDateForInput(sale.start_date),
      end_date: fixDateForInput(sale.end_date),
      active: (sale.status || sale.sale_status)?.toLowerCase() === 'active' || 
              (sale.status || sale.sale_status)?.toLowerCase() === 'ongoing',
    });
    
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setIsEditMode(false);
    setEditingSaleId(null);
    setSelectedProduct(null);
    setProductSearchStr('');
    setProductVendorSearchStr('');
    
    // Reset dates to reasonable defaults
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    
    const dayAfterTomorrow = new Date(tomorrow);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
    dayAfterTomorrow.setHours(22, 0, 0, 0);
    
    setFormData({
      product_id: '',
      product_price: '',
      start_date: fixDateForInput(tomorrow.toISOString()),
      end_date: fixDateForInput(dayAfterTomorrow.toISOString()),
      active: true,
    });
    setFormErrors({});
  };

  const handleProductSelect = (event: any, value: Product | null) => {
    setSelectedProduct(value);
    if (value) {
      setFormData(prev => ({
        ...prev,
        product_id: (value.product_id || value.id || '').toString(),
        product_price: value.product_price?.toString() || '',
      }));
      if (formErrors.product_id) {
        setFormErrors(prev => ({
          ...prev,
          product_id: undefined
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        product_id: '',
        product_price: '',
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof FlashSaleFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      active: e.target.checked
    }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<FlashSaleFormData> = {};

    if (!formData.product_id.trim()) {
      errors.product_id = 'Product is required';
    }
    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      errors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) {
      errors.end_date = 'End date must be after start date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddFlashSale = async () => {
    if (!hasFlashSaleWrite) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to add flash sales.',
        severity: 'error'
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!selectedProduct) {
      setSnackbar({
        open: true,
        message: 'Please select a product.',
        severity: 'error'
      });
      return;
    }

    try {
      const payload = {
        product_id: Number(formData.product_id),
        product_price: formData.product_price || selectedProduct.product_price || 0,
        start_date: formData.start_date,
        end_date: formData.end_date,
        active: formData.active ? 1 : 0,
      };

      let result: any;

      if (isEditMode && editingSaleId) {
        const updatePayload = {
          id: editingSaleId,
          product_id: Number(formData.product_id),
          product_price: formData.product_price || selectedProduct.product_price || 0,
          start_date: formData.start_date,
          end_date: formData.end_date,
          status: formData.active ? 'active' : 'inactive',
        };
        console.log('📤 Updating flash sale payload:', updatePayload);
        result = await updateFlashSale(updatePayload).unwrap();
        console.log('✅ Update flash sale result:', result);

        // Immediately update local state so table reflects new values without waiting for refetch
        setEnrichedFlashSales(prev =>
          prev.map(s =>
            (s.id || s.flash_sale_id) === editingSaleId
              ? {
                  ...s,
                  product_price: String(updatePayload.product_price),
                  start_date: updatePayload.start_date,
                  end_date: updatePayload.end_date,
                  status: updatePayload.status,
                  sale_status: updatePayload.status,
                }
              : s
          )
        );
      } else {
        console.log('📤 Adding flash sale payload:', payload);
        result = await addFlashSale(payload).unwrap();
        console.log('✅ Add flash sale result:', result);
      }
      
      setSnackbar({
        open: true,
        message: isEditMode 
          ? (result.message || 'Flash sale updated successfully!')
          : (result.message || 'Flash sale added successfully!'),
        severity: 'success'
      });
      refetch();
      handleCloseAddDialog();
    } catch (err: any) {
      console.error('❌ Failed to add/update flash sale:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || `Error ${isEditMode ? 'updating' : 'adding'} flash sale. Please try again.`,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (saleId: number) => {
    if (!hasFlashSaleWrite) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to delete flash sales.',
        severity: 'error'
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this flash sale?')) {
      try {
        console.log('🗑️ Deleting flash sale ID:', saleId);
        
        const numericId = Number(saleId);
        if (isNaN(numericId)) {
          throw new Error('Invalid flash sale ID');
        }
        
        const result = await deleteFlashSale(numericId).unwrap();
        console.log('✅ Delete result:', result);
        
        setSnackbar({
          open: true,
          message: result.message || 'Flash sale deleted successfully!',
          severity: 'success'
        });
        
        refetch();
      } catch (err: any) {
        console.error('❌ Failed to delete flash sale:', err);
        setSnackbar({
          open: true,
          message: err?.data?.message || err?.message || 'Error deleting flash sale. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const isLoadingData = isLoading || isLoadingAllProducts;
  const totalRecords = enrichedFlashSales.length;

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223', mb: 0.5 }}>
                    Flash Sales
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage time-limited special offers on products
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoadingData}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  {hasFlashSaleWrite && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleOpenAddDialog}
                      sx={{
                        bgcolor: '#008060',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      Add Flash Sale
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Main Content Card */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                {/* Tabs Section */}
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(e, v) => setTabValue(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#008060',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`All flash sales (${getTabCount(0)})`} />
                    <Tab label={`Active (${getTabCount(1)})`} />
                    <Tab label={`Upcoming (${getTabCount(2)})`} />
                    <Tab label={`Expired (${getTabCount(3)})`} />
                  </Tabs>
                </Box>

                {/* Filter Section */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search product name, product ID, sale ID..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{ 
                      minWidth: 300,
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    size="small"
                    placeholder="Filter vendor by name or ID..."
                    value={vendorSearchStr}
                    onChange={(e) => setVendorSearchStr(e.target.value)}
                    sx={{ 
                      minWidth: 280,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterList fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {searchQuery && (
                    <Chip
                      label={`Found: ${filteredSales.length} results`}
                      onDelete={() => setSearchQuery('')}
                      sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }}
                    />
                  )}
                </Box>

                {/* Loading State */}
                {isLoadingData && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress size={40} sx={{ color: '#008060', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading flash sales...
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Error State */}
                {isError && !isLoadingData && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">
                      Failed to load flash sales. Please try again.
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={() => refetch()}
                        sx={{ ml: 1 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  </Box>
                )}

                {/* Empty State */}
                {!isLoadingData && !isError && filteredSales.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <Box sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: '50%', 
                      bgcolor: '#fff4e6', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3
                    }}>
                      <Whatshot sx={{ fontSize: 48, color: '#ff9800' }} />
                    </Box>
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      {searchQuery ? 'No flash sales found' : 'No flash sales yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchQuery 
                        ? 'Try adjusting your search query or filters'
                        : 'Get started by adding your first flash sale'
                      }
                    </Typography>
                    {hasFlashSaleWrite && !searchQuery && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenAddDialog}
                        sx={{
                          bgcolor: '#008060',
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': { bgcolor: '#006e52' }
                        }}
                      >
                        Add Flash Sale
                      </Button>
                    )}
                  </Box>
                )}

                {/* Table Content */}
                {!isLoadingData && filteredSales.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Table Header */}
                    {!isMobile && (
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '60px 80px 2fr 1fr 1fr 1fr 1fr auto',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          #
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Image
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Product
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Price
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Sale Price
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Period
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>
                    )}

                    {/* Table Rows */}
                    {filteredSales.map((sale: EnrichedFlashSale, index: number) => (
                      <Box
                        key={sale.id || sale.flash_sale_id || index}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr auto' : '60px 80px 2fr 1fr 1fr 1fr 1fr auto',
                          gap: 2,
                          px: 2,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: index < filteredSales.length - 1 ? '1px solid #e1e3e5' : 'none',
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background-color 0.15s ease',
                        }}
                      >
                        {!isMobile ? (
                          <>
                            {/* Index */}
                            <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>
                              {index + 1}
                            </Typography>

                            {/* Product Image */}
                            <Avatar
                              src={sale.product_image || sale.product_main_image ? 
                                `https://fftestservice.sarsetech.com/images/${sale.product_image || sale.product_main_image}` : undefined}
                              variant="rounded"
                              sx={{ width: 48, height: 48, bgcolor: '#f1f2f4' }}
                            >
                              {(sale.product_name || sale.prod_name)?.[0] || '?'}
                            </Avatar>

                            {/* Product Info */}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', mb: 0.5 }}>
                                {sale.product_name || sale.prod_name || 'Product'}
                              </Typography>
                              <Chip 
                                label={`ID: ${sale.product_id}`}
                                size="small"
                                sx={{ 
                                  bgcolor: '#e3f2fd',
                                  color: '#1565c0',
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  height: 20
                                }}
                              />
                              {sale.vendor_id !== null && sale.vendor_id !== undefined ? (
                                <>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Vendor: {sale.vendor_name || 'Vendor'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Vendor ID: {sale.vendor_id}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  Vendor: Fortune Five (Own Product)
                                </Typography>
                              )}
                            </Box>

                            {/* Original Price */}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                {formatCurrency(sale.original_price || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Original
                              </Typography>
                            </Box>

                            {/* Sale Price */}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                {formatCurrency(sale.product_price)}
                              </Typography>
                              {sale.original_price && sale.original_price > 0 && (
                                <Chip
                                  label={`-${calculateDiscount(sale.original_price, parseFloat(sale.product_price))}%`}
                                  size="small"
                                  sx={{ 
                                    bgcolor: '#ffebee',
                                    color: '#c62828',
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    height: 20,
                                    mt: 0.5
                                  }}
                                />
                              )}
                            </Box>

                            {/* Period */}
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <CalendarToday sx={{ fontSize: 12 }} />
                                {formatDateTime(sale.start_date)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                to {formatDateTime(sale.end_date)}
                              </Typography>
                            </Box>

                            {/* Status */}
                            <Box>
                              <Chip 
                                label={sale.sale_status || sale.status || 'Unknown'} 
                                size="small"
                                icon={
                                  (sale.sale_status || sale.status)?.toLowerCase() === 'active' || 
                                  (sale.sale_status || sale.status)?.toLowerCase() === 'ongoing' ? 
                                    <Whatshot sx={{ fontSize: 16, color: '#00783e !important' }} /> : undefined
                                }
                                sx={{
                                  fontSize: '12px',
                                  ...getStatusColor(sale.sale_status || sale.status)
                                }}
                              />
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              {hasFlashSaleWrite && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditFlashSale(sale)}
                                    sx={{ 
                                      color: '#6d7175',
                                      '&:hover': { 
                                        bgcolor: '#e3f2fd',
                                        color: '#1565c0'
                                      }
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(sale.id || sale.flash_sale_id || 0)}
                                    disabled={isDeleting}
                                    sx={{ 
                                      color: '#6d7175',
                                      '&:hover': { 
                                        bgcolor: '#ffebee',
                                        color: '#c62828'
                                      }
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          </>
                        ) : (
                          // Mobile View
                          <>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              <Avatar
                                src={sale.product_image || sale.product_main_image ? 
                                  `https://fftestservice.sarsetech.com/images/${sale.product_image || sale.product_main_image}` : undefined}
                                variant="rounded"
                                sx={{ width: 56, height: 56, bgcolor: '#f1f2f4' }}
                              >
                                {(sale.product_name || sale.prod_name)?.[0] || '?'}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', mb: 0.5 }}>
                                  {sale.product_name || sale.prod_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip 
                                    label={`ID: ${sale.product_id}`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: '#e3f2fd',
                                      color: '#1565c0',
                                      fontWeight: 600,
                                      fontSize: '10px',
                                      height: 18
                                    }}
                                  />
                                  <Chip 
                                    label={sale.sale_status || sale.status || 'Unknown'} 
                                    size="small"
                                    sx={{
                                      fontSize: '10px',
                                      height: 18,
                                      ...getStatusColor(sale.sale_status || sale.status)
                                    }}
                                  />
                                </Box>
                                {getVendorId(sale) !== null && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Vendor: {getVendorName(sale) || 'Vendor'} (ID: {getVendorId(sale)})
                                  </Typography>
                                )}
                                {getVendorId(sale) === null && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Vendor: Fortune Five (Own Product)
                                  </Typography>
                                )}

                                <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                    {formatCurrency(sale.original_price || 0)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                    {formatCurrency(sale.product_price)}
                                  </Typography>
                                  {sale.original_price && sale.original_price > 0 && (
                                    <Chip
                                      label={`-${calculateDiscount(sale.original_price, parseFloat(sale.product_price))}%`}
                                      size="small"
                                      sx={{ 
                                        bgcolor: '#ffebee',
                                        color: '#c62828',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                        height: 18
                                      }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {formatDateTime(sale.start_date)} - {formatDateTime(sale.end_date)}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                              {hasFlashSaleWrite && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditFlashSale(sale)}
                                    sx={{ 
                                      color: '#6d7175',
                                      '&:hover': { 
                                        bgcolor: '#e3f2fd',
                                        color: '#1565c0'
                                      }
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(sale.id || sale.flash_sale_id || 0)}
                                    disabled={isDeleting}
                                    sx={{ 
                                      color: '#6d7175',
                                      '&:hover': { 
                                        bgcolor: '#ffebee',
                                        color: '#c62828'
                                      }
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Flash Sale Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Whatshot sx={{ color: '#ff9800' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                {isEditMode ? 'Edit Flash Sale' : 'Add Flash Sale'}
              </Typography>
              {isEditMode && (
                <Chip 
                  label="Editing" 
                  size="small" 
                  sx={{ 
                    bgcolor: '#ff9800', 
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20,
                    ml: 1
                  }} 
                />
              )}
            </Box>
            <IconButton onClick={handleCloseAddDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Vendor Search & Product Select */}
            <TextField
              size="small"
              label="Search Vendor by Name or ID"
              placeholder="Enter vendor name or vendor ID"
              value={productVendorSearchStr}
              onChange={(e) => setProductVendorSearchStr(e.target.value)}
              fullWidth
              disabled={isEditMode}
              helperText={isEditMode ? 'Vendor cannot be changed in edit mode' : 'Only products belonging to the searched vendor will be shown'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Product Search & Select */}
            <Autocomplete
              options={productOptions}
              getOptionLabel={(option) => `${option.prod_name || 'Product'} (ID: ${option.product_id || option.id})${getVendorId(option) !== null ? ` • Vendor: ${getVendorName(option) || 'Vendor'} (ID: ${getVendorId(option)})` : ' • Vendor: Fortune Five (Own Product)'}`}
              value={selectedProduct}
              onChange={handleProductSelect}
              onInputChange={(event, newInputValue) => {
                setProductSearchStr(newInputValue);
              }}
              loading={isLoadingProducts}
              disabled={isEditMode} // Disable product selection in edit mode
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search & Select Product *"
                  error={!!formErrors.product_id}
                  helperText={formErrors.product_id || (isEditMode ? 'Product cannot be changed in edit mode' : 'Type to search for products')}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Avatar
                    src={option.product_main_image ? `https://fftestservice.sarsetech.com/images/${option.product_main_image}` : undefined}
                    variant="rounded"
                    sx={{ width: 40, height: 40, mr: 2 }}
                  >
                    {option.prod_short_name?.[0] || option.prod_name?.[0] || 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.prod_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Product ID: {option.product_id || option.id} | Price: {formatCurrency(option.product_price || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getVendorId(option) !== null ? `Vendor: ${getVendorName(option) || 'Vendor'} (ID: ${getVendorId(option)})` : 'Vendor: Fortune Five (Own Product)'}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText={
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {productVendorSearchStr.trim()
                      ? `No products found for vendor "${productVendorSearchStr.trim()}".`
                      : 'No products found. Try a different search term.'}
                  </Typography>
                </Box>
              }
            />

            {/* Selected Product Preview */}
            {selectedProduct && (
              <Card sx={{ borderRadius: '12px', border: '2px solid #e3f2fd', p: 2, bgcolor: '#f8fbff' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1565c0' }}>
                  {isEditMode ? 'Current Product:' : 'Selected Product:'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={selectedProduct.product_main_image ? `https://fftestservice.sarsetech.com/images/${selectedProduct.product_main_image}` : undefined}
                    variant="rounded"
                    sx={{ width: 64, height: 64, border: '2px solid #e3f2fd' }}
                  >
                    {selectedProduct.prod_short_name?.[0] || selectedProduct.prod_name?.[0] || 'P'}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {selectedProduct.prod_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Product ID: {selectedProduct.product_id || selectedProduct.id}
                    </Typography>
                    {getVendorId(selectedProduct) !== null ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Vendor: {getVendorName(selectedProduct) || 'Vendor'} (ID: {getVendorId(selectedProduct)})
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Vendor: Fortune Five (Own Product)
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      Original Price: {formatCurrency(selectedProduct.product_price || 0)}
                    </Typography>
                    {isEditMode && (
                      <Typography variant="body2" sx={{ mt: 1, color: '#ff9800' }}>
                        Sale Price: {formatCurrency(formData.product_price)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Card>
            )}

            {/* Sale Price Input (only in edit mode) */}
            {isEditMode && selectedProduct && (
              <TextField
                label="Flash Sale Price *"
                name="product_price"
                type="number"
                fullWidth
                value={formData.product_price}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                     AED
                    </InputAdornment>
                  ),
                }}
                helperText={`Original price: ${formatCurrency(selectedProduct.product_price || 0)}`}
              />
            )}

            {/* Date Range */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Start Date *"
                name="start_date"
                type="datetime-local"
                fullWidth
                value={formData.start_date}
                onChange={handleInputChange}
                error={!!formErrors.start_date}
                helperText={formErrors.start_date}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: new Date().toISOString().slice(0, 16) // Prevent past dates
                }}
              />
              
              <TextField
                label="End Date *"
                name="end_date"
                type="datetime-local"
                fullWidth
                value={formData.end_date}
                onChange={handleInputChange}
                error={!!formErrors.end_date}
                helperText={formErrors.end_date}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: formData.start_date || new Date().toISOString().slice(0, 16)
                }}
              />
            </Box>

            {/* Active Toggle */}
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              bgcolor: formData.active ? '#d1f4e0' : '#ffcdd2',
              border: `1px solid ${formData.active ? '#00783e' : '#c62828'}`,
              transition: 'all 0.3s ease'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleActiveChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formData.active ? 'Active' : 'Inactive'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.active 
                        ? 'Flash sale will be active during the specified period' 
                        : 'Flash sale will be saved but not active'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

           
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
           <Button
            onClick={handleCloseAddDialog}
            disabled={isAdding || isUpdating}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#eeeeee',
              bgcolor: '#f44336'
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleCloseAddDialog}
            disabled={isAdding || isUpdating}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#eeeeee',
              bgcolor: '#F78E12'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddFlashSale} 
            variant="contained"
            disabled={isAdding || isUpdating || !selectedProduct}
            sx={{
              bgcolor: '#008060',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': { bgcolor: '#006e52' },
              '&:disabled': {
                bgcolor: '#c9cccf',
                color: '#6d7175'
              }
            }}
          >
            {isAdding || isUpdating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                {isEditMode ? 'Updating...' : 'Adding...'}
              </Box>
            ) : (
              isEditMode ? 'Update Flash Sale' : 'Add Flash Sale'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FlashSale;